/**
 * Utilitário para validação de horário de funcionamento das áreas de entrega
 * 
 * Este módulo fornece funções para verificar se uma área de entrega está
 * aberta/fechada considerando o timezone configurado.
 */

// Dias da semana em inglês para mapeamento com JavaScript Date
const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * Obtém a data/hora atual no timezone especificado
 * 
 * @param {string} timezone - Timezone (ex: 'America/Sao_Paulo')
 * @returns {Object} { dayOfWeek, hours, minutes, currentTimeString }
 */
export function getCurrentTimeInTimezone(timezone) {
  try {
    const now = new Date();
    
    // Formata a data no timezone especificado
    const options = {
      timeZone: timezone,
      weekday: 'long',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    };
    
    const formatter = new Intl.DateTimeFormat('en-US', options);
    const parts = formatter.formatToParts(now);
    
    const weekdayPart = parts.find(p => p.type === 'weekday');
    const hourPart = parts.find(p => p.type === 'hour');
    const minutePart = parts.find(p => p.type === 'minute');
    
    const weekday = weekdayPart?.value?.toLowerCase() || 'monday';
    const hours = parseInt(hourPart?.value || '0', 10);
    const minutes = parseInt(minutePart?.value || '0', 10);
    
    // Formata o horário atual como string HH:MM
    const currentTimeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    
    return {
      dayOfWeek: weekday,
      hours,
      minutes,
      currentTimeString,
    };
  } catch (error) {
    console.error('Error getting current time in timezone:', error);
    // Fallback para horário local
    const now = new Date();
    const dayIndex = now.getDay();
    
    return {
      dayOfWeek: DAY_KEYS[dayIndex],
      hours: now.getHours(),
      minutes: now.getMinutes(),
      currentTimeString: `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`,
    };
  }
}

/**
 * Converte um horário string (HH:MM) para minutos desde meia-noite
 * 
 * @param {string} timeString - Horário no formato "HH:MM"
 * @returns {number} Minutos desde meia-noite
 */
export function timeStringToMinutes(timeString) {
  if (!timeString || typeof timeString !== 'string') {
    return 0;
  }
  
  const [hours, minutes] = timeString.split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
}

/**
 * Verifica se o horário atual está dentro do período de funcionamento
 * 
 * @param {string} currentTime - Horário atual "HH:MM"
 * @param {string} openTime - Horário de abertura "HH:MM"
 * @param {string} closeTime - Horário de fechamento "HH:MM"
 * @returns {boolean} true se está dentro do horário
 */
export function isTimeInRange(currentTime, openTime, closeTime) {
  const current = timeStringToMinutes(currentTime);
  const open = timeStringToMinutes(openTime);
  const close = timeStringToMinutes(closeTime);
  
  // Se o horário de fechamento é menor que o de abertura,
  // significa que cruza a meia-noite (ex: 22:00 - 02:00)
  if (close < open) {
    // Está aberto se: depois da abertura OU antes do fechamento
    return current >= open || current <= close;
  }
  
  // Caso normal: abertura antes do fechamento
  return current >= open && current <= close;
}

/**
 * Verifica se uma área de entrega está aberta no momento atual
 * 
 * @param {Object} businessHours - Configuração de horários da área
 * @param {string} businessHours.timezone - Timezone (ex: 'America/Sao_Paulo')
 * @param {Object} businessHours.schedule - Horários por dia da semana
 * @returns {Object} { isOpen, message, dayOfWeek, currentTime, schedule }
 */
export function isDeliveryAreaOpen(businessHours) {
  // Se não tem horários configurados, considera sempre aberta
  if (!businessHours || !businessHours.schedule) {
    return {
      isOpen: true,
      message: 'Horários não configurados - sempre aberta',
      hasSchedule: false,
    };
  }
  
  const { timezone = 'America/Sao_Paulo', schedule } = businessHours;
  
  // Obtém horário atual no timezone configurado
  const { dayOfWeek, currentTimeString } = getCurrentTimeInTimezone(timezone);
  
  // Obtém configuração do dia atual
  const daySchedule = schedule[dayOfWeek];
  
  if (!daySchedule) {
    return {
      isOpen: false,
      message: `Dia ${dayOfWeek} não configurado`,
      dayOfWeek,
      currentTime: currentTimeString,
      hasSchedule: true,
    };
  }
  
  // Se o dia está desabilitado
  if (!daySchedule.enabled) {
    // Encontra o próximo dia aberto
    const nextOpenDay = findNextOpenDay(schedule, dayOfWeek);
    
    return {
      isOpen: false,
      message: nextOpenDay 
        ? `Fechado hoje. Reabre ${nextOpenDay.dayLabel} às ${nextOpenDay.openTime}`
        : 'Fechado',
      dayOfWeek,
      currentTime: currentTimeString,
      hasSchedule: true,
    };
  }
  
  // Verifica se está no horário de funcionamento
  const { open, close } = daySchedule;
  const isOpen = isTimeInRange(currentTimeString, open, close);
  
  if (isOpen) {
    return {
      isOpen: true,
      message: `Aberto até ${close}`,
      dayOfWeek,
      currentTime: currentTimeString,
      schedule: daySchedule,
      hasSchedule: true,
    };
  } else {
    // Está fechado - verifica se ainda vai abrir hoje ou próximo dia
    const currentMinutes = timeStringToMinutes(currentTimeString);
    const openMinutes = timeStringToMinutes(open);
    
    if (currentMinutes < openMinutes) {
      // Ainda vai abrir hoje
      return {
        isOpen: false,
        message: `Fechado. Abre hoje às ${open}`,
        dayOfWeek,
        currentTime: currentTimeString,
        schedule: daySchedule,
        hasSchedule: true,
      };
    } else {
      // Já fechou hoje, mostra próximo dia aberto
      const nextOpenDay = findNextOpenDay(schedule, dayOfWeek);
      
      return {
        isOpen: false,
        message: nextOpenDay 
          ? `Fechado. Reabre ${nextOpenDay.dayLabel} às ${nextOpenDay.openTime}`
          : 'Fechado',
        dayOfWeek,
        currentTime: currentTimeString,
        schedule: daySchedule,
        hasSchedule: true,
      };
    }
  }
}

/**
 * Encontra o próximo dia aberto a partir de um dia
 * 
 * @param {Object} schedule - Configuração de horários
 * @param {string} currentDay - Dia atual (ex: 'monday')
 * @returns {Object|null} { dayKey, dayLabel, openTime } ou null
 */
function findNextOpenDay(schedule, currentDay) {
  const dayLabels = {
    sunday: 'Domingo',
    monday: 'Segunda',
    tuesday: 'Terça',
    wednesday: 'Quarta',
    thursday: 'Quinta',
    friday: 'Sexta',
    saturday: 'Sábado',
  };
  
  const currentIndex = DAY_KEYS.indexOf(currentDay);
  
  // Verifica os próximos 7 dias
  for (let i = 1; i <= 7; i++) {
    const nextIndex = (currentIndex + i) % 7;
    const nextDay = DAY_KEYS[nextIndex];
    const dayConfig = schedule[nextDay];
    
    if (dayConfig && dayConfig.enabled) {
      return {
        dayKey: nextDay,
        dayLabel: dayLabels[nextDay],
        openTime: dayConfig.open,
      };
    }
  }
  
  return null;
}

/**
 * Valida se uma área de entrega está aberta para receber pedidos
 * Esta é a função principal para usar na validação de endereço
 * 
 * @param {Object} area - Área de entrega do Firebase
 * @param {Object} area.businessHours - Configuração de horários
 * @returns {Object} { isValid, message }
 */
export function validateDeliveryAreaBusinessHours(area) {
  if (!area) {
    return {
      isValid: false,
      message: 'Área não encontrada',
    };
  }
  
  // Se a área não está ativa, não aceita pedidos
  if (!area.active) {
    return {
      isValid: false,
      message: 'Esta área não está aceitando pedidos no momento',
    };
  }
  
  // Verifica horário de funcionamento
  const result = isDeliveryAreaOpen(area.businessHours);
  
  return {
    isValid: result.isOpen,
    message: result.message,
    businessHoursInfo: result,
  };
}

export default {
  getCurrentTimeInTimezone,
  timeStringToMinutes,
  isTimeInRange,
  isDeliveryAreaOpen,
  validateDeliveryAreaBusinessHours,
};
